/**
 * The period summary ("did it help?") under the Findings scope bar, shown
 * while the scope has a period: what is new, what is still happening,
 * what later runs did not see (and what was never re-run), and what was
 * verified or regressed in the period. Each count opens the Queue on
 * exactly those findings (`?pb=<bucket>`).
 *
 * "Not seen in later runs" claims absence in a re-run only: a finding is
 * verified by a re-run that resolves it, never by this summary. "Not
 * re-run yet" lists the scenarios to run next.
 */
import type { FindingsPeriodBucket, FindingsScope } from "@iamu/shared";
import { Link, useSearchParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { STALE_TIMES } from "@/lib/query-performance";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useScopeBuilds } from "./findings-scope-bar";
import { parseScope, periodLabel, scopeParams } from "./scope-url-state";

/** Queue param carrying the selected bucket. */
export const PERIOD_BUCKET_PARAM = "pb";

const BUCKETS: {
  key: FindingsPeriodBucket;
  label: string;
  hint: string;
}[] = [
  { key: "new", label: "New", hint: "First seen in the period" },
  {
    key: "still",
    label: "Still happening",
    hint: "Seen before and again in it",
  },
  {
    key: "not_seen",
    label: "Not seen in later runs",
    hint: "Its scenario re-ran, no run observed it",
  },
  {
    key: "not_rerun",
    label: "Not re-run yet",
    hint: "None of its scenarios ran since",
  },
  { key: "verified", label: "Verified", hint: "A re-run resolved it" },
  { key: "regressed", label: "Regressed", hint: "Came back after a fix" },
];

export function FindingsPeriodSummary({ projectId }: { projectId: string }) {
  const [params] = useSearchParams();
  const scope = parseScope(params);
  const { selectedLabel } = useScopeBuilds(projectId, scope.period);
  const { data, isLoading } = trpc.finding.periodSummary.useQuery(
    { projectId, scope },
    { enabled: !!scope.period, staleTime: STALE_TIMES.list },
  );
  if (!scope.period) return null;

  const active = params.get(PERIOD_BUCKET_PARAM);
  const base = scopeParams(params);
  const hrefFor = (bucket: FindingsPeriodBucket | null) => {
    const p = new URLSearchParams(base);
    if (bucket) p.set(PERIOD_BUCKET_PARAM, bucket);
    const q = p.toString();
    return q ? `?${q}` : "?";
  };
  const period = periodLabel(scope.period, selectedLabel);

  return (
    <section aria-labelledby="period-summary-title" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="period-summary-title" className="text-md font-semibold">
          {period}: did it help?
        </h2>
        {active && (
          <Link
            to={hrefFor(null)}
            className="text-sm text-foreground underline underline-offset-4 hover:no-underline"
          >
            Show the full queue
          </Link>
        )}
      </div>
      <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-3 lg:grid-cols-6">
        {BUCKETS.map((b) => {
          const selected = active === b.key;
          const n = data?.[b.key];
          return (
            <li key={b.key} className="bg-card">
              <Link
                to={hrefFor(selected ? null : b.key)}
                aria-current={selected ? "true" : undefined}
                className={cn(
                  "flex h-full flex-col gap-0.5 px-4 py-3 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                  selected &&
                    "bg-muted shadow-[inset_0_-2px_0_var(--foreground)]",
                )}
              >
                {isLoading || n == null ? (
                  <Skeleton className="h-7 w-8" />
                ) : (
                  <output
                    className={cn(
                      "text-lg font-semibold tabular-nums",
                      n === 0 && "text-muted-foreground",
                    )}
                  >
                    {n}
                  </output>
                )}
                <span className="text-sm font-semibold text-foreground">
                  {b.label}
                </span>
                <span className="text-xs text-muted-foreground">{b.hint}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        Not seen in later runs counts runs, not fixes: a finding is verified
        only when a re-run resolves it.
      </p>
      {active === "not_rerun" && (
        <RunNext projectId={projectId} scope={scope} />
      )}
    </section>
  );
}

/** The scenarios to run next for the "not re-run yet" findings. */
function RunNext({
  projectId,
  scope,
}: {
  projectId: string;
  scope: FindingsScope;
}) {
  const { data, isLoading } = trpc.finding.periodRerunScenarios.useQuery(
    { projectId, scope },
    { staleTime: STALE_TIMES.list },
  );
  if (isLoading) return <Skeleton className="h-16 w-full" />;
  if (!data?.length) return null;
  return (
    <div className="space-y-1 rounded-lg border bg-card px-4 py-3">
      <h3 className="text-sm font-semibold text-foreground">
        Run next to check these findings
      </h3>
      <ul className="divide-y divide-border/60">
        {data.map((s) => (
          <li
            key={s.scenarioId}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2"
          >
            <Link
              to={`/projects/${projectId}/scenarios/${s.outlineId}#persona-${s.personaId}`}
              className="text-base font-semibold text-foreground underline-offset-4 hover:underline"
            >
              {s.scenarioName}
              <span className="font-normal text-muted-foreground">
                {" "}
                as {s.personaName}
              </span>
            </Link>
            <output className="text-xs text-muted-foreground tabular-nums">
              {s.findingCount} {s.findingCount === 1 ? "finding" : "findings"}
            </output>
          </li>
        ))}
      </ul>
    </div>
  );
}
